/**
 * useCompanySignIn
 * Handles the "Sign in with your company" OAuth flow.
 * Looks up the org by slug, gets the allowedEmailDomain, then triggers Google OAuth
 * with the `hd` hint pre-filled. Real access control is enforced server-side.
 */

import { useState, useCallback, useRef } from 'react'
import { Platform } from 'react-native'
import * as WebBrowser from 'expo-web-browser'
import { supabase } from '../../lib/supabase'
import { useDialog } from '../../shared/hooks/useDialog'
import { logger } from '../../helpers/logger.helper'
import {
  B2B_GOOGLE_OAUTH_QUERY_PARAMS,
  OAUTH_FLOW_STORAGE_KEY,
  OAUTH_POST_AUTH_PATH_STORAGE_KEY,
} from '../constants/oauth.constants'
import { getOAuthRedirectUrls, parseSupabaseOAuthRedirectUrl } from '../helpers/signInApi'
import { clearStaleLocalSessionBeforeOAuth } from '../helpers/clearStaleOAuthSession.helper'
import { exchangeOAuthCodeForSessionOnce } from '../helpers/oauthPkceExchange.helper'
import {
  clearOAuthAutoRetryState,
  persistOAuthRestartConfig,
} from '../helpers/oauthAutoRetry.helper'
import { POST_AUTH_DEFAULT_PATH } from '../helpers/postAuthRedirect.helper'
import { API_URL } from '../../../constants/api'

export interface OrgPublicInfo {
  id: string
  name: string
  displayName: string | null
  logoUrl: string | null
  allowedEmailDomain: string | null
}

interface UseCompanySignInReturn {
  slug: string
  setSlug: (v: string) => void
  org: OrgPublicInfo | null
  lookupLoading: boolean
  lookupError: string | null
  signInLoading: boolean
  lookupOrg: () => Promise<void>
  handleCompanyGoogleAuth: () => Promise<void>
}

export function useCompanySignIn(): UseCompanySignInReturn {
  const { showAlert } = useDialog()
  const [slug, setSlug] = useState('')
  const [org, setOrg] = useState<OrgPublicInfo | null>(null)
  const [lookupLoading, setLookupLoading] = useState(false)
  const [lookupError, setLookupError] = useState<string | null>(null)
  const [signInLoading, setSignInLoading] = useState(false)
  const inFlightRef = useRef(false)

  const lookupOrg = useCallback(async () => {
    const trimmed = slug.trim().toLowerCase()
    if (!trimmed) {
      setLookupError('Enter your company ID to continue.')
      return
    }
    setLookupError(null)
    setOrg(null)
    setLookupLoading(true)
    try {
      const res = await fetch(`${API_URL}/accounts/public/${encodeURIComponent(trimmed)}`)
      const json = await res.json()
      if (!res.ok || !json.success) {
        setLookupError('Organization not found. Check the ID and try again.')
        return
      }
      setOrg(json.data as OrgPublicInfo)
    } catch (err) {
      logger.warn('[useCompanySignIn] lookup failed', { message: err instanceof Error ? err.message : String(err) })
      setLookupError('Could not reach the server. Check your connection.')
    } finally {
      setLookupLoading(false)
    }
  }, [slug])

  const handleCompanyGoogleAuth = useCallback(async () => {
    if (!org) return
    if (inFlightRef.current) return
    inFlightRef.current = true
    setSignInLoading(true)
    try {
      await clearStaleLocalSessionBeforeOAuth()
      clearOAuthAutoRetryState()
      const redirectTo = getOAuthRedirectUrls()?.redirectUrl ?? undefined
      const queryParams = org.allowedEmailDomain
        ? B2B_GOOGLE_OAUTH_QUERY_PARAMS(org.allowedEmailDomain)
        : { prompt: 'select_account' }

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          ...(redirectTo ? { redirectTo } : {}),
          queryParams,
        },
      })
      if (error) throw error

      if (Platform.OS === 'web' && data?.url) {
        try {
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(OAUTH_FLOW_STORAGE_KEY, 'sign_in')
            sessionStorage.setItem(OAUTH_POST_AUTH_PATH_STORAGE_KEY, POST_AUTH_DEFAULT_PATH)
            persistOAuthRestartConfig({ provider: 'google', queryParams })
          }
        } catch {
          // ignore storage errors
        }
        window.location.href = data.url
        return
      }

      if (Platform.OS !== 'web' && data?.url && redirectTo) {
        const result = await WebBrowser.openAuthSessionAsync(data.url, redirectTo)
        if (result.type === 'success' && result.url) {
          const tokens = parseSupabaseOAuthRedirectUrl(result.url)
          if (tokens) {
            const { error: sessionError } = await supabase.auth.setSession({
              access_token: tokens.access_token,
              refresh_token: tokens.refresh_token,
            })
            if (sessionError) throw sessionError
          } else if (result.url.includes('code=')) {
            const exchangeResult = await exchangeOAuthCodeForSessionOnce(result.url)
            if (exchangeResult.error) throw exchangeResult.error
          }
        }
      }
    } catch (err) {
      logger.error('[useCompanySignIn] Google auth failed', { message: err instanceof Error ? err.message : String(err) })
      await showAlert({
        title: 'Sign in failed',
        message: 'Could not sign in with Google. Please try again.',
        type: 'default',
      })
    } finally {
      inFlightRef.current = false
      setSignInLoading(false)
    }
  }, [org, showAlert])

  return {
    slug,
    setSlug,
    org,
    lookupLoading,
    lookupError,
    signInLoading,
    lookupOrg,
    handleCompanyGoogleAuth,
  }
}
